import { MessageDto } from '@act/shared/data-transfer-objects';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import styled from 'styled-components';
import { phoneFormatter } from 'utilities/formatters/phone-formatter';

import { reemaColors } from '../../../toolkit/styles/colors';
import TextsmsOutlinedIcon from '@mui/icons-material/TextsmsOutlined';

import { stringFormatter } from 'utilities/formatters/stringFormatter';
import { useState } from 'react';
import { themeColors } from 'toolkit/styles/colors';
import ErrorAlertIcon from '@mui/icons-material/ErrorOutline';
import {
  ClockIcon,
  PaperPlaneTiltIcon,
  TrashIcon,
  PencilIcon,
} from '@phosphor-icons/react';
import { Button } from '@act/storybook';
import { timeFormatter } from '@act/shared/utilities';
import { TrackingProvider, useTracking } from 'react-components/tracking';

const StyledCardContent = styled(CardContent)`
  font-size: 14px;
  .info-pane {
    font-size: 16px;
    padding: 16px 20px;
    border-radius: 6px;
    border: 1px solid #dce4e8;
    h6 {
      color: #70787c;
      margin: 12px 0 0;
      &:first-child {
        margin: 0;
      }
    }
    p {
      margin: 0 0 4px;
    }
  }
  p {
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
`;
const CardHeader = styled.div`
  display: grid;
  font-size: 16px;
  grid-template-columns: 48px 1fr auto;
  gap: 12px;
  margin: 16px;
  .title {
    display: grid;
    align-items: center;
    grid-template-columns: auto auto 1fr;
    gap: 8px;
    h4 {
      margin: 0;
      font-weight: 500;
      font-size: 16px;
    }
  }
  p {
    margin: 4px 0 0;
    line-height: 20px;
    font-size: 14px;
  }
`;

export default function MessageItem({
  message,
  onEdit,
  onDelete,
  onSend,
}: {
  message: MessageDto;
  onEdit: (message: MessageDto) => void;
  onDelete: (message: MessageDto) => void;
  onSend: (message: MessageDto) => void;
}) {
  const { generateTrackingProps } = useTracking();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const handleEdit = () => {
    onEdit(message);
    setConfirmSend(false);
  };
  const handleDelete = () => {
    onDelete(message);
    setConfirmDelete(false);
  };

  const theme = useTheme();

  const getButtonSet = () => {
    if (confirmDelete) {
      return (
        <>
          <Button
            debounced
            onClick={() => setConfirmDelete(false)}
            trackingName="cancel-delete-button"
          >
            Cancel
          </Button>
          <Button
            debounced
            onClick={handleDelete}
            trackingName="confirm-delete-button"
            sx={{
              backgroundColor: themeColors.error,
              color: 'white',
              '&:hover': {
                color: themeColors.error,
              },
            }}
          >
            Delete
          </Button>
        </>
      );
    }
    if (confirmSend) {
      return (
        <>
          <Button
            debounced
            onClick={() => setConfirmSend(false)}
            trackingName="cancel-send-button"
          >
            Cancel
          </Button>
          <Button
            debounced
            onClick={() => onSend(message)}
            trackingName="confirm-send-button"
            sx={{
              backgroundColor: themeColors.primary,
              color: 'white',
              '&:hover': {
                color: themeColors.primary,
              },
            }}
          >
            Send
          </Button>
        </>
      );
    }
    return (
      <>
        <div
          style={{ cursor: 'pointer', padding: '8px' }}
          onClick={handleEdit}
          {...generateTrackingProps('edit-message-button')}
        >
          <PencilIcon style={{ width: '24px', height: '24px' }} />
        </div>
        <div
          style={{ cursor: 'pointer', padding: '8px' }}
          onClick={() => setConfirmSend(true)}
          {...generateTrackingProps('send-message-button')}
        >
          <PaperPlaneTiltIcon style={{ width: '24px', height: '24px' }} />
        </div>
        <div
          style={{ cursor: 'pointer', padding: '8px' }}
          onClick={() => setConfirmDelete(true)}
          {...generateTrackingProps('delete-message-button')}
        >
          <TrashIcon style={{ width: '24px', height: '24px' }} />
        </div>
      </>
    );
  };

  return (
    <TrackingProvider contextName="message-item">
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: '16px',
          padding: '8px',
          borderColor: confirmDelete
            ? themeColors.error
            : themeColors.surfaceVariant,
        }}
      >
        <CardHeader>
          <div className="icon">
            <Avatar
              sx={{
                bgcolor: reemaColors.primaryContainer,
                color: reemaColors.black,
                fontSize: '24px',
                width: 48,
                height: 48,
              }}
              aria-label="icon"
            >
              {<TextsmsOutlinedIcon></TextsmsOutlinedIcon>}
            </Avatar>
          </div>
          <div className="content">
            <div
              className="title"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <h4>Scheduled text</h4>
              <div
                className="chips"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '10px',
                }}
              >
                <ClockIcon style={{ height: '20px', width: '20px' }} />
                <Typography>
                  {timeFormatter.epochToHumanReadableDate({
                    date: message.sendAt,
                    includeTime: true,
                  })}
                </Typography>
              </div>
            </div>
            <p>{phoneFormatter.stringToPretty(message.smsPhoneNumber)}</p>
          </div>
        </CardHeader>
        <StyledCardContent
          style={{ backgroundColor: theme.palette.navyShades[50] }}
        >
          <Typography variant="bodyMedium">
            {stringFormatter.truncate(message.text, 200)}
          </Typography>
          {confirmDelete && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: '16px',
              }}
            >
              <ErrorAlertIcon sx={{ color: themeColors.error }} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  paddingLeft: '10px',
                }}
              >
                <Typography
                  variant="bodyMedium"
                  sx={{ color: themeColors.error, fontWeight: 'bold' }}
                >
                  Delete this scheduled message?
                </Typography>
                <Typography variant="bodySmall">
                  This action cannot be undone.
                </Typography>
              </Box>
            </Box>
          )}
          {confirmSend && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: '16px',
              }}
            >
              <ErrorAlertIcon sx={{ color: themeColors.primary }} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  paddingLeft: '10px',
                }}
              >
                <Typography
                  variant="bodyMedium"
                  sx={{ color: themeColors.primary, fontWeight: 'bold' }}
                >
                  Send this scheduled message?
                </Typography>
                <Typography variant="bodySmall">
                  This action cannot be undone.
                </Typography>
              </Box>
            </Box>
          )}
        </StyledCardContent>

        <div
          className="actions"
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          {getButtonSet()}
        </div>
      </Card>
    </TrackingProvider>
  );
}
